import { useMemo, useCallback } from 'react';
import { Grid, Button, Divider } from '@mui/material';
import { Formik } from 'formik';
import yup from '../../../app/utils/customYup';
import { toast } from 'react-toastify';

import EditProfileForm from './EditProfileForm';
import EditModal, { IEditModalProps } from '../../../components/EditModal';

import useLocale from '../../../app/hooks/useLocale';

import { useDebtorProfileQuery, useApplicationFileQuery, useDebtorProfileMutation } from '../queries';
import { regexPhone } from '../../../app/utils/helpers';
import {
  getDebtorProfileFormValues,
  convertDebtorProfileFormValuesToDto,
  isMaskedAddress,
  isMaskedPhone,
} from '../utils';
import { IDebtorProfileFormValues } from '../types';

interface IEditDebtModalProps extends Pick<IEditModalProps, 'onCancel'> {
  onOk?: () => void;
}

const EditProfileModal = ({ onCancel, onOk }: IEditDebtModalProps) => {
  const { t } = useLocale();

  const { data: appFileData } = useApplicationFileQuery() || {};
  const { data: debtorProfile } = useDebtorProfileQuery() || {};

  const { mutate: updateDebtorProfile } = useDebtorProfileMutation() || {};

  const getRequiredByLabel = useCallback(
    (fieldName: string) => t.FIELD_IS_REQUIRED?.replace('{0}', fieldName),
    [t.FIELD_IS_REQUIRED]
  );

  // Check if address is masked from backend
  const isAddressMasked = isMaskedAddress(debtorProfile?.profile?.address);
  // Check if phone is masked from backend
  const isPhoneMasked = isMaskedPhone(debtorProfile?.profile?.mainPhoneNumber);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        firstName: yup.string().required(getRequiredByLabel(t.FIRST_NAME)),
        lastName: yup.string().required(getRequiredByLabel(t.LAST_NAME)),
        // Date of birth is always disabled in profile page, so never required
        dateOfBirth: yup.date().nullable().typeError(t.INVALID_DATE).max(new Date(), t.DATE_CANNOT_BE_IN_THE_FUTURE),
        // Only require address if it's not already provided (i.e., if it was masked and cleared)
        address: isAddressMasked ? yup.string().nullable() : yup.string().required(getRequiredByLabel(t.ADDRESS)),
        mainPhoneTypeId: yup.string().required(getRequiredByLabel(t.TYPE)),
        // Only require and validate phone if it's not already provided (i.e., if it was masked and cleared)
        mainPhoneNumber: isPhoneMasked
          ? yup.string().nullable()
          : yup.string().required(getRequiredByLabel(t.PHONE_NUMBER)).matches(regexPhone, t.PHONE_NOT_VALID),
      }),
    [
      getRequiredByLabel,
      isAddressMasked,
      isPhoneMasked,
      t.ADDRESS,
      t.DATE_CANNOT_BE_IN_THE_FUTURE,
      t.FIRST_NAME,
      t.INVALID_DATE,
      t.LAST_NAME,
      t.PHONE_NOT_VALID,
      t.PHONE_NUMBER,
      t.TYPE,
    ]
  );

  const initialValues = useMemo(() => getDebtorProfileFormValues(debtorProfile?.profile), [debtorProfile?.profile]);

  const handleSubmit = useCallback(
    (values?: IDebtorProfileFormValues) => {
      updateDebtorProfile(
        { fileId: appFileData?.id, ...convertDebtorProfileFormValuesToDto(values) },
        {
          onSuccess: () => {
            toast.success(t.DEBTOR_PROFILE_IS_UPDATED);
            onOk?.();
          },
        }
      );
    },
    [appFileData?.id, onOk, t.DEBTOR_PROFILE_IS_UPDATED, updateDebtorProfile]
  );

  return (
    <EditModal title={t.EDIT_PROFILE} onCancel={onCancel}>
      <Formik
        initialValues={initialValues}
        onSubmit={(_: IDebtorProfileFormValues, { setSubmitting }) => {
          setSubmitting(false);
        }}
        validationSchema={validationSchema}
      >
        {({ isValid, values }) => (
          <>
            <EditProfileForm />

            <Divider sx={{ mt: 2, mb: 2 }} />

            <Grid container flexDirection="row" gap={2} flex={1} justifyContent="center">
              <Grid item mobile={6} tablet={4}>
                <Button variant="contained" fullWidth disabled={!isValid} onClick={() => handleSubmit(values)}>
                  {t.SAVE}
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </Formik>
    </EditModal>
  );
};

export default EditProfileModal;
